// components/notifications/NotificationSettingsPanel.tsx
'use client';

import React, { memo, useCallback, useMemo, useState } from 'react';
import Switch from '@/components/ui/Switch';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { normalizePositiveInteger } from '@/kernel/dailyCreditUsageAlert';
import type { DailyCreditUsageNotificationPreference } from '#types/types';

const FALLBACK_TIMEZONE = 'Europe/Paris';
const BASE_TIMEZONE_OPTIONS = [
  'UTC',
  'Europe/Paris',
  'Europe/London',
  'America/New_York',
  'America/Los_Angeles',
  'Asia/Dubai',
  'Asia/Singapore',
];

const PANEL_FONT_STYLE = { fontFamily: 'ui-sans-serif, -apple-system, system-ui, sans-serif' };

const resolveBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || FALLBACK_TIMEZONE;
  } catch {
    return FALLBACK_TIMEZONE;
  }
};

interface NotificationSettingsPanelProps {
  embedded?: boolean;
}

function NotificationSettingsPanel({ embedded = false }: NotificationSettingsPanelProps) {
  const { kernel } = useKernel();
  const userPreferences = useKernelStore((state) => state.userPreferences);
  const language = useKernelStore((state) => state.language);
  const dailyCreditUsageAlert = userPreferences.notificationPreferences?.dailyCreditUsageAlert;
  const browserTimezone = useMemo(() => resolveBrowserTimezone(), []);
  const timezoneOptions = useMemo(() => {
    const options = new Set(BASE_TIMEZONE_OPTIONS);
    if (browserTimezone) options.add(browserTimezone);
    if (dailyCreditUsageAlert?.timezone) options.add(dailyCreditUsageAlert.timezone);
    return Array.from(options).filter(Boolean).sort((left, right) => left.localeCompare(right));
  }, [browserTimezone, dailyCreditUsageAlert?.timezone]);

  const [enabled, setEnabled] = useState(dailyCreditUsageAlert?.enabled === true);
  const [thresholdCredits, setThresholdCredits] = useState<number | null>(dailyCreditUsageAlert?.thresholdCredits ?? 50);
  const [repeatStepCredits, setRepeatStepCredits] = useState<number | null>(dailyCreditUsageAlert?.repeatStepCredits ?? 25);
  const [timezone, setTimezone] = useState(dailyCreditUsageAlert?.timezone || browserTimezone);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const persistDailyCreditUsageAlert = useCallback(async (patch: Partial<DailyCreditUsageNotificationPreference>) => {
    const hasKey = (key: keyof DailyCreditUsageNotificationPreference) => Object.prototype.hasOwnProperty.call(patch, key);
    const requestedAlert: DailyCreditUsageNotificationPreference = {
      enabled: patch.enabled ?? enabled,
      thresholdCredits: hasKey('thresholdCredits') ? patch.thresholdCredits : thresholdCredits,
      repeatStepCredits: hasKey('repeatStepCredits') ? patch.repeatStepCredits : repeatStepCredits,
      timezone: hasKey('timezone') ? patch.timezone : timezone,
    };

    setErrorMessage(null);
    setIsSaving(true);
    try {
      const result = await kernel.current?.saveDailyCreditUsageAlert(requestedAlert, browserTimezone);
      if (result && !result.ok) setErrorMessage(result.error);
    } finally {
      setIsSaving(false);
    }
  }, [browserTimezone, enabled, kernel, repeatStepCredits, thresholdCredits, timezone]);

  const handleEnabledChange = useCallback((checked: boolean) => {
    setEnabled(checked);
    void persistDailyCreditUsageAlert({ enabled: checked });
  }, [persistDailyCreditUsageAlert]);

  const handleThresholdChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setThresholdCredits(Number(event.target.value));
    setErrorMessage(null);
  }, []);

  const handleThresholdCommit = useCallback(() => {
    const normalized = normalizePositiveInteger(thresholdCredits ?? 50) ?? 50;
    setThresholdCredits(normalized);
    void persistDailyCreditUsageAlert({ thresholdCredits: normalized });
  }, [persistDailyCreditUsageAlert, thresholdCredits]);

  const handleRepeatStepChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value.trim();
    const parsed = rawValue.length > 0 ? Number(rawValue) : null;
    const normalized = normalizePositiveInteger(parsed);
    setRepeatStepCredits(normalized);
  }, []);

  const handleRepeatStepCommit = useCallback(() => {
    void persistDailyCreditUsageAlert({ repeatStepCredits });
  }, [persistDailyCreditUsageAlert, repeatStepCredits]);

  const handleTimezoneChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextTimezone = event.target.value;
    setTimezone(nextTimezone);
    void persistDailyCreditUsageAlert({ timezone: nextTimezone });
  }, [persistDailyCreditUsageAlert]);

  const copy = language === 'fr'
    ? {
        title: 'Notifications',
        subtitle: "Configurez les alertes liées à l'utilisation de votre compte.",
        section: 'Utilisation quotidienne des crédits',
        enabled: 'Alerte de consommation quotidienne',
        enabledDescription: 'Recevez une alerte lorsque votre consommation dépasse un seuil.',
        threshold: 'Seuil quotidien',
        thresholdDescription: 'Premier niveau de consommation qui déclenche une alerte.',
        repeat: 'Répéter tous les',
        repeatDescription: 'Laissez vide pour ne pas répéter les alertes.',
        timezone: 'Fuseau horaire',
        timezoneDescription: 'Définit la limite locale de chaque journée.',
        universalTime: 'Temps universel coordonné',
        credits: 'crédits',
      }
    : {
        title: 'Notifications',
        subtitle: 'Configure alerts for events related to your account usage.',
        section: 'Daily credit usage',
        enabled: 'Daily usage alert',
        enabledDescription: 'Notify me when my daily usage exceeds a threshold.',
        threshold: 'Daily threshold',
        thresholdDescription: 'First daily usage level that creates an alert.',
        repeat: 'Repeat every',
        repeatDescription: 'Leave empty to avoid repeated alerts.',
        timezone: 'Timezone',
        timezoneDescription: 'Defines the local boundary for each usage day.',
        universalTime: 'Coordinated universal time',
        credits: 'credits',
      };

  const fieldClassName = 'h-7 w-[132px] rounded-full border border-[#2A2A2A] bg-transparent px-2 text-right font-mono text-[12px] text-white/85 outline-none transition-colors hover:bg-white/[0.04] focus:border-white/20 disabled:opacity-45';

  return (
    <div className={embedded ? '' : 'px-10 py-8'} style={PANEL_FONT_STYLE}>
      <header className="mb-8">
        <h1 className="text-[22px] font-semibold leading-7 tracking-[-0.02em] text-white/95">{copy.title}</h1>
        <p className="mt-1 text-[13px] leading-5 text-white/55">{copy.subtitle}</p>
      </header>

      <section className="mb-8">
        <h2 className="mb-2 border-b border-white/[0.08] pb-3 text-[14px] font-semibold text-white/90">{copy.section}</h2>
        <div className="flex min-h-[52px] flex-col items-start gap-2.5 py-2.5 sm:flex-row sm:items-center sm:gap-6">
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-medium text-white/85">{copy.enabled}</div>
            <p className="mt-0.5 text-[12px] leading-[18px] text-white/45">{copy.enabledDescription}</p>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={handleEnabledChange}
            disabled={isSaving}
            ariaLabel={copy.enabled}
            labelRight={null}
          />
        </div>

        <div className="flex min-h-[52px] flex-col items-start gap-2.5 py-2.5 sm:flex-row sm:items-center sm:gap-6">
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-medium text-white/85">{copy.threshold}</div>
            <p className="mt-0.5 text-[12px] leading-[18px] text-white/45">{copy.thresholdDescription}</p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              max={10000}
              value={thresholdCredits ?? ''}
              onChange={handleThresholdChange}
              onBlur={handleThresholdCommit}
              disabled={isSaving}
              className={fieldClassName}
              aria-label={copy.threshold}
            />
            <span className="w-12 text-[11px] text-white/35">{copy.credits}</span>
          </div>
        </div>

        <div className="flex min-h-[52px] flex-col items-start gap-2.5 py-2.5 sm:flex-row sm:items-center sm:gap-6">
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-medium text-white/85">{copy.repeat}</div>
            <p className="mt-0.5 text-[12px] leading-[18px] text-white/45">{copy.repeatDescription}</p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              max={10000}
              value={repeatStepCredits ?? ''}
              onChange={handleRepeatStepChange}
              onBlur={handleRepeatStepCommit}
              disabled={isSaving}
              placeholder={'\u2014'}
              className={fieldClassName}
              aria-label={copy.repeat}
            />
            <span className="w-12 text-[11px] text-white/35">{copy.credits}</span>
          </div>
        </div>

        <div className="flex min-h-[52px] flex-col items-start gap-2.5 py-2.5 sm:flex-row sm:items-center sm:gap-6">
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-medium text-white/85">{copy.timezone}</div>
            <p className="mt-0.5 text-[12px] leading-[18px] text-white/45">{copy.timezoneDescription}</p>
          </div>
          <select
            value={timezone}
            onChange={handleTimezoneChange}
            disabled={isSaving}
            className="h-7 max-w-[210px] rounded-full border border-[#2A2A2A] bg-transparent px-2 text-[12px] font-medium text-white/85 outline-none transition-colors hover:bg-white/[0.04] focus:border-white/20 disabled:opacity-45"
            aria-label={copy.timezone}
          >
            {timezoneOptions.map((option) => (
              <option key={option} value={option}>
                {option === 'UTC' ? copy.universalTime : option}
              </option>
            ))}
          </select>
        </div>
      </section>

      {errorMessage ? (
        <div className="mt-4 rounded-md border border-[#FF7369]/20 bg-[#FF7369]/10 px-3 py-2 text-[12px] text-[#FF7369]">
          {errorMessage}
        </div>
      ) : null}
      {isSaving ? (
        <div className="mt-4 text-right text-[11px] text-white/35">Saving changes…</div>
      ) : null}
    </div>
  );
}

export default memo(NotificationSettingsPanel);
